<ul class="ti-tally">
  <li><strong data-test="count-create">{{ counts.Create }}</strong> to add</li>
  <li><strong data-test="count-update">{{ counts.Update }}</strong> to update</li>
  <li><strong data-test="count-skip">{{ counts.Skip }}</strong> held back</li>
  <li *ngIf="counts.Blocked">
    <strong data-test="count-blocked">{{ counts.Blocked }}</strong>
    cannot be imported
  </li>
  <li class="br-text-muted">of {{ total }} rows</li>
</ul>

<!-- One statement per column. A column that is never written has no row-level
     fix, so a row number would be information nobody can act on. -->
<section *ngIf="unwritable.length" class="ti-section ti-section--aside"
         data-test="unwritable-columns">
  <h3 class="ti-section-title">Columns this file has that we will not write</h3>
  <ul class="ti-unwritable">
    <li *ngFor="let column of unwritable" [attr.data-column]="column.field">
      <strong>{{ column.header }}</strong>
      <span *ngIf="column.computed">
        &mdash; we work this one out ourselves, so a value here is replaced at
        the next recalculation rather than kept.
      </span>
      <span *ngIf="!column.computed">
        &mdash; there is nowhere for it to go on our side. It is exported so
        the file reads whole; it is not read back.
      </span>
      <span class="br-text-muted br-small" *ngIf="column.rows">
        {{ column.rows }} of {{ total }} rows have a value here.
      </span>
    </li>
  </ul>
</section>

<section *ngIf="worthALook.length" class="ti-section ti-section--caution"
         data-test="worth-a-look">
  <h3 class="ti-section-title">
    Going in, but worth a look
    <span class="br-text-muted br-small">{{ worthALookTotal }}</span>
  </h3>
  <ul class="ti-rows">
    <li *ngFor="let item of worthALook" class="ti-row"
        [attr.data-row]="item.row.rowNo">
      <span class="ti-row-no">{{ item.row.rowNo }}</span>
      <span class="ti-row-id">{{ identify(item.row) }}</span>
      <span class="ti-row-say">
        <span *ngFor="let note of item.notes" class="ti-note">
          {{ noteCopy(note).sentence }}
        </span>
        <span class="br-text-muted" *ngIf="item.row.detail">{{ item.row.detail }}</span>
      </span>
    </li>
  </ul>
  <p class="br-text-muted br-small ti-more" *ngIf="worthALookHidden"
     data-test="worth-a-look-more">
    and {{ worthALookHidden }} more like these, all going in.
  </p>
</section>

<section *ngIf="heldBack.length" class="ti-section">
  <h3 class="ti-section-title">Held back</h3>
  <div *ngFor="let group of heldBack" class="ti-group"
       [attr.data-reason]="group.reason">
    <div class="ti-group-head">
      <button type="button" class="ti-disclose"
              (click)="toggleOpen(group.reason)"
              [attr.aria-expanded]="isOpen(group.reason)">
        <span class="ti-caret">{{ isOpen(group.reason) ? '▾' : '▸' }}</span>
        {{ group.copy.title }}
        <span class="br-text-muted br-small">{{ group.rows.length }}</span>
      </button>

      <!-- One answer for the class, so the rows nobody scrolled to are
           decided on purpose rather than by running out of patience. -->
      <span class="ti-group-answer">
        <button type="button" class="br-btn br-btn--sm"
                [class.br-btn--primary]="group.answer !== null"
                [class.br-btn--outline]="group.answer === null"
                (click)="answerClass(group.reason,
                                     group.answer === null ? 'Create' : null)"
                [attr.data-test]="'import-all-' + group.reason">
          {{ group.answer === null
             ? 'Import all of these'
             : 'Importing all of these' }}
        </button>
      </span>
    </div>

    <p class="br-text-muted br-small ti-group-say">{{ group.copy.sentence }}</p>

    <ul class="ti-rows" *ngIf="isOpen(group.reason)">
      <li *ngFor="let row of group.shown" class="ti-row"
          [attr.data-row]="row.rowNo">
        <span class="ti-row-no">{{ row.rowNo }}</span>
        <span class="ti-row-id">{{ identify(row) }}</span>
        <span class="br-text-muted ti-row-say" *ngIf="row.detail">
          {{ row.detail }}
        </span>
        <!-- The exception on top of the class answer, in both directions. -->
        <button type="button" class="br-btn br-btn--sm br-btn--outline"
                (click)="answerRow(row.rowNo,
                                   actionOf(row) === 'Skip' ? 'Create' : 'Skip')"
                [attr.data-test]="'row-toggle-' + row.rowNo">
          {{ actionOf(row) === 'Skip' ? 'Import this one' : 'Leave this one out' }}
        </button>
      </li>
    </ul>
    <!-- The class control above acts on the REASON, so it covers these too.
         What they do not get is the per-row exception, which a list nobody
         could scroll was not offering either. -->
    <p class="br-text-muted br-small ti-more" *ngIf="group.hidden"
       [attr.data-test]="'more-' + group.reason">
      and {{ group.hidden }} more held back for this reason, answered with
      the rest of the group.
    </p>
  </div>
</section>

<section *ngIf="refused.length" class="ti-section">
  <h3 class="ti-section-title">Cannot be imported</h3>
  <p class="br-text-muted br-small">
    These rows are not valid as they stand, so there is nothing to decide here.
    Correcting them in the file and importing again is the way in.
  </p>
  <!-- Deliberately no control. Blocked is not a preference, and offering a
       button the rules would refuse would be worse than offering none. -->
  <div *ngFor="let group of refused" class="ti-group"
       [attr.data-reason]="group.reason">
    <div class="ti-group-head">
      <button type="button" class="ti-disclose"
              (click)="toggleOpen(group.reason)"
              [attr.aria-expanded]="isOpen(group.reason)">
        <span class="ti-caret">{{ isOpen(group.reason) ? '▾' : '▸' }}</span>
        {{ group.copy.title }}
        <span class="br-text-muted br-small">{{ group.rows.length }}</span>
      </button>
    </div>
    <p class="br-text-muted br-small ti-group-say">{{ group.copy.sentence }}</p>
    <ul class="ti-rows" *ngIf="isOpen(group.reason)">
      <li *ngFor="let row of group.shown" class="ti-row"
          [attr.data-row]="row.rowNo">
        <span class="ti-row-no">{{ row.rowNo }}</span>
        <span class="ti-row-id">{{ identify(row) }}</span>
        <span class="br-text-muted ti-row-say" *ngIf="row.detail">
          {{ row.detail }}
        </span>
      </li>
    </ul>
    <p class="br-text-muted br-small ti-more" *ngIf="group.hidden"
       [attr.data-test]="'more-' + group.reason">
      and {{ group.hidden }} more refused for this reason.
    </p>
  </div>
</section>
